"use client";

import React, { useState } from "react";
import {
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Button,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Image, { StaticImageData } from "next/image";
import { useRouter } from "next/navigation";
import userIcon from "../../../assets/lucid-icon/user.svg";
import orderIcon from "../../../assets/lucid-icon/shopping-bag.svg";
import creditIcon from "../../../assets/lucid-icon/credit-card.svg";
import walletIcon from "../../../assets/lucid-icon/wallet.svg";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import { logout } from "@/store/slices/authSlice";

const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE;

interface ProfileDrawerProps {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
}

type MenuItem = {
  text: string;
  icon: StaticImageData;
  path: string;
};

const ProfileDrawer: React.FC<ProfileDrawerProps> = ({
  open,
  onClose,
  onOpen,
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { bulkUserID, userData } = useAppSelector((state) => state.auth);
  const { availableCredit } = useAppSelector((state) => state.payment);
  const isOnboarded = bulkUserID && bulkUserID?.length > 0;
  const [loading, setLoading] = useState(false);

  const handleNavigation = (path: string) => {
    router.push(path);
    onClose();
  };

  const menuItems: MenuItem[] = [
    { text: "My Profile", icon: userIcon, path: "/user/profile" },
    { text: "My Orders", icon: orderIcon, path: "/user/orders" },
    ...(availableCredit?.status
      ? [
          {
            text: "Line of Credit",
            icon: creditIcon,
            path: "/user/lineofcredit",
          },
        ]
      : []),
    { text: "Wallet", icon: walletIcon, path: "/user/wallet" },
  ];

  const handleConfirmLogout = () => {
    setLoading(true);
    setTimeout(() => {
      dispatch(logout());
      window.location.reload();
    }, 1000);
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          borderRadius: "16px 16px 0 0",
          overflow: "hidden",
        },
      }}
    >
      <div className="p-5 bg-white-1 flex flex-col items-center relative w-full rounded-t-2xl">
        <IconButton onClick={onClose} className="!absolute top-2 right-2">
          <Close />
        </IconButton>

        <div className="flex flex-col items-center mt-4">
          <Image
            src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_USER_IMAGE/${
              userData?.UserImage || ""
            }`}
            alt="User"
            width={75}
            height={75}
            className="rounded-full"
          />
          <div
            className={`flex flex-col items-center ${
              isOnboarded ? "" : "blur-sm"
            }`}
          >
            <h3 className="text-lg font-semibold mt-2">
              {userData?.FirstName} {userData?.LastName}
            </h3>
            <p className="text-sm text-gray-500">{userData?.Email}</p>
          </div>

          {!isOnboarded && (
            <Button
              variant="contained"
              color="warning"
              sx={{ marginTop: "8px", padding: "3px 10px", textTransform: "none" }}
              onClick={() => handleNavigation("/onboarding")}
            >
              Update KYC
            </Button>
          )}
        </div>

        <List className="w-full mt-4">
          {menuItems.map((item, index) => (
            <React.Fragment key={item.text}>
              <ListItem
                component="button"
                onClick={() => handleNavigation(item.path)}
                className="hover:bg-gray-100 rounded-md transition-all duration-200"
              >
                <ListItemIcon sx={{ minWidth: "40px" }}>
                  <Image src={item.icon} alt={item.text} width={24} height={24} />
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  className="font-medium"
                  sx={{ fontFamily: "inherit !important" }}
                />
              </ListItem>
              {index !== menuItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>

        {/* Logout Button with Spinner */}
        <Button
          variant="contained"
          color="error"
          disabled={loading}
          sx={{
            marginTop: "24px",
            width: "100%",
            maxWidth: "95%",
            padding: "6px 10px",
            textTransform: "none",
            fontFamily: "inherit",
            fontSize: "18px",
            fontWeight: "600",
            letterSpacing: "1px",
            borderRadius: "30px",
          }}
          onClick={handleConfirmLogout}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Logout"}
        </Button>
      </div>
    </SwipeableDrawer>
  );
};

export default ProfileDrawer;
